import { monthsAbrev } from "./date/date";

/**
 * O card de valor por JANELA: a soma dos meses de um intervalo, dos dois lados.
 *
 * No modo ACUMULADO (preferencia da empresa) o card soma o periodo que esta nas barras do grafico —
 * o intervalo da barra de periodo, Ago/25–Ago/26 — e, sem comparacao ativa, nao mostra variacao.
 * Na comparacao "todos os meses" as barras do ano base vao de janeiro ate o ultimo mes com dado, e
 * o outro ano entra com os MESMOS meses: somar Set–Dez so de um lado faria a variacao medir
 * calendario, nao desempenho.
 *
 * O acumulado do calculatrix (metrica `a` do ponto) nao serve aqui: so as series contabeis do cenario
 * ORIGINAL tem `a` calculado (o saldo final da conta); nas fiscais, de pessoal, fin, Simples e eSocial
 * o back preenche `a` com o proprio `m` (`point-result-map.repository.ts` no data-engine). Medido em
 * 05/10/2026: o "acumulado" de Ago/2026 do Faturamento fiscal vinha igual ao valor do mes. Por isso a
 * soma e feita aqui, sobre os pontos mensais que o card ja le.
 */

/**
 * O ultimo mes (1..12) com dado no ano, ou `null` se o ano nao tem nenhum.
 * @param {Array<string|Date>} availableDates datas disponiveis da fonte, no cenario base
 * @param {number} year
 */
export function lastMonthWithData(availableDates = [], year) {
    const meses = (availableDates || [])
        .map((d) => new Date(d))
        .filter((d) => !isNaN(d) && d.getUTCFullYear() === year)
        .map((d) => d.getUTCMonth() + 1);
    return meses.length ? Math.max(...meses) : null;
}

/**
 * O que os "data values" (os cards de valor) mostram, por empresa: `preferences.dataValuesMode`,
 * gravado pelo popover "Base de dados". Ausente vale MENSAL — o card de sempre; o acumulado e
 * escolha da empresa.
 */
export const DATA_VALUES_MODES = { accumulated: "accumulated", monthly: "monthly" };

export function dataValuesMode(preferences = {}) {
    return preferences?.dataValuesMode === DATA_VALUES_MODES.accumulated
        ? DATA_VALUES_MODES.accumulated
        : DATA_VALUES_MODES.monthly;
}

const normalizar = (texto = "") =>
    String(texto)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase();

/**
 * Se a serie soma mes a mes. So FLUXO soma. O que e saldo ou taxa compara o ultimo mes com o mesmo
 * mes do outro lado:
 * - percentual (a margem de jan + a de fev nao e margem de nada);
 * - numero e dias (indice, headcount, prazo medio);
 * - serie ja marcada como saldo (`metadata.accumulated`);
 * - "Acumulado do periodo" (`metadata.accumulation: 'period'`): a calculatrix ja devolve cada mes somado
 *   desde o inicio do periodo — somar de novo aqui dobraria o valor;
 * - conta PATRIMONIAL do contabil (classificador 1 ou 2 — caixa, estoque, fornecedores). So CONTA do
 *   plano: na serie derivada (linha da DRE, indicador composto) o `classifier` e a POSICAO da linha —
 *   "Receita Bruta" e a linha 1 — e nao diz nada sobre ser saldo;
 * - SALDO do financeiro. O fin nao marca saldo em lugar nenhum: o que separa "Saldo de Caixa" de
 *   "Variacao de Caixa" e so o nome. Heuristica assumida — serie de saldo com outro nome soma.
 * - MEDIA em moeda ("Ticket medio", "Salario medio", "Custo por funcionario"): tambem so pelo nome.
 */
export function sumsMonthByMonth(serie = {}) {
    if (["percentage", "number", "day"].includes(serie.unit)) return false;
    if (serie.metadata?.accumulated) return false;
    if (serie.metadata?.accumulation === "period") return false;
    const fonte = Array.isArray(serie.sources) ? serie.sources[0] : serie.sources;
    if (
        fonte === "accounting" &&
        serie.genus !== "derivative" &&
        /^[12]/.test(String(serie.classifier ?? ""))
    )
        return false;
    const nome = normalizar(serie.name_nd || serie.name);
    if (fonte === "fin" && nome.includes("SALDO")) return false;
    if (/\bMEDI[OA]S?\b|\bPOR (FUNCIONARIO|COLABORADOR|EMPREGADO|CLIENTE)\b/.test(nome))
        return false;
    return true;
}

const mesDe = (ano, mes) =>
    new Date(`${ano}/${String(mes).padStart(2, "0")}/01`);

/** Os meses de `start` a `end`, inclusive, como datas do primeiro dia. */
export function monthsBetween(start, end) {
    const meses = [];
    let ano = start.getUTCFullYear();
    let mes = start.getUTCMonth() + 1;
    const fim = end.getUTCFullYear() * 12 + end.getUTCMonth();
    while (ano * 12 + (mes - 1) <= fim) {
        meses.push(mesDe(ano, mes));
        mes += 1;
        if (mes > 12) {
            mes = 1;
            ano += 1;
        }
    }
    return meses;
}

/**
 * A JANELA de cada lado do card, ou `null` quando o card compara ponto com ponto (o de sempre).
 *
 * - Acumulado, sem comparacao: o periodo das barras (`startDate` a `baseDate`), SEM variacao
 *   (`showVariation: false`) — ninguem escolheu com o que comparar, e a % contra o ano anterior lia
 *   como afirmacao que a tela nao fez. O lado `comparation` vem igual ao base so para manter a forma.
 * - Comparacao "todos os meses": de janeiro ate o ultimo mes com dado no ano base, nos dois anos.
 *   No modo mensal, so esse ultimo mes contra o mesmo mes do outro ano — a data que o dashboard grava
 *   nesse modo e dezembro, que o ano base ainda nao tem.
 * - Comparacao de um mes so: `null`. O grafico tem duas barras, o mes e o comparado.
 * - Mensal sem "todos os meses": `null`.
 *
 * @returns {null | { base: { start: Date, end: Date }, comparation: { start: Date, end: Date }, sumMonths: boolean, showVariation: boolean }}
 */
export function accumulationWindow({
    mode,
    baseDate,
    startDate,
    comparation = {},
    lastMonthAvailable = null
}) {
    const acumulado = mode === DATA_VALUES_MODES.accumulated;
    if (comparation.allMonths) {
        if (!lastMonthAvailable || !comparation.date) return null;
        const anoBase = baseDate.getUTCFullYear();
        const anoComparado = comparation.date.getUTCFullYear();
        return {
            base: {
                start: mesDe(anoBase, 1),
                end: mesDe(anoBase, lastMonthAvailable)
            },
            comparation: {
                start: mesDe(anoComparado, 1),
                end: mesDe(anoComparado, lastMonthAvailable)
            },
            sumMonths: acumulado,
            showVariation: true
        };
    }
    if (!acumulado || comparation.active) return null;
    const inicio =
        startDate && startDate <= baseDate ? startDate : baseDate;
    const base = {
        start: mesDe(inicio.getUTCFullYear(), inicio.getUTCMonth() + 1),
        end: mesDe(baseDate.getUTCFullYear(), baseDate.getUTCMonth() + 1)
    };
    return { base, comparation: base, sumMonths: true, showVariation: false };
}

/** Graficos de valor unico que, no modo acumulado, somam a janela em vez de ler so o mes base. */
export const WINDOW_SUMMED_CHARTS = ["pie", "variablepie"];

/**
 * A janela da PIZZA no modo acumulado — a mesma do card sem comparacao: o periodo das barras. Sem
 * isso a fatia lia so o mes base enquanto o card de cima somava o periodo, e a mesma conta aparecia
 * com dois numeros na tela.
 *
 * `null` quando a pizza le o mes base, o de sempre: modo mensal, comparacao ligada ou "todos os
 * meses" (a pizza vira colunas), periodo que nao e mensal (o ponto ja vem somado), orcamento, dado
 * ficticio, ou o toggle acumulado da visualizacao (ele troca o valor do mes pela metrica `a`, que
 * nao se soma).
 *
 * `seriesIds` sao as series que somam (`sumsMonthByMonth`); as outras — saldo, percentual, media —
 * continuam no mes base, como no card. Precisa da serie COMPLETA (`sources`, `genus`,
 * `classifier`): a que o grafico recebe vem sem esses campos.
 *
 * @returns {null | { start: Date, end: Date, scenarioId: string, seriesIds: string[] }}
 */
export function chartAccumulation({
    category,
    mode,
    period = {},
    comparation = {},
    budget = false,
    fakeData = false,
    visualizationAccumulated = false,
    series = []
} = {}) {
    if (!WINDOW_SUMMED_CHARTS.includes(category)) return null;
    if (budget || fakeData || visualizationAccumulated) return null;
    if (period.period !== "monthly" || !period.date) return null;
    if (comparation.active || comparation.allMonths) return null;
    const janela = accumulationWindow({
        mode,
        baseDate: period.date,
        startDate: period.startDate
    });
    if (!janela) return null;
    const seriesIds = (series || [])
        .filter((serie) => serie && sumsMonthByMonth(serie))
        .map((serie) => serie._id);
    if (!seriesIds.length) return null;
    return {
        start: janela.base.start,
        end: janela.base.end,
        scenarioId: period.scenario?._id,
        seriesIds
    };
}

const indiceDoMes = (data) => {
    const d = new Date(data);
    return d.getUTCFullYear() * 12 + d.getUTCMonth();
};

/**
 * A soma dos pontos de uma serie de `start` a `end`, inclusive, num cenario. Compara por mes, nao
 * por instante: o ponto vem em `03:00Z` e a janela nasce na meia-noite local.
 *
 * @param {Array<{ s: string, sc?: string, t: string|Date }>} points
 * @param {(point: object) => number} valueOf o valor do ponto, como o grafico le
 */
export function sumPointsInRange(
    points = [],
    { serieId, scenarioId, start, end },
    valueOf = (point) => point?.m?.v
) {
    const inicio = indiceDoMes(start);
    const fim = indiceDoMes(end);
    return (points || []).reduce((total, point) => {
        if (!point || point.s != serieId) return total;
        if (scenarioId && point.sc && point.sc !== scenarioId) return total;
        const mes = indiceDoMes(point.t);
        if (mes < inicio || mes > fim) return total;
        return total + (Number(valueOf(point)) || 0);
    }, 0);
}

const mesAno = (data) =>
    `${monthsAbrev(data.getUTCMonth())}/${data.getUTCFullYear()}`;

/**
 * "Ago/2025 até Ago/2026" para quem soma mais de um mes; "Ago/2026" para quem nao soma (o card
 * mostra o ultimo mes do intervalo) ou para intervalo de um mes so.
 */
export function rangeLabel({ start, end }, sums) {
    if (!sums || +start === +end) return mesAno(end);
    return `${mesAno(start)} até ${mesAno(end)}`;
}

/** O tooltip do numero: "Acumulado de Ago/2025 até Ago/2026", ou so o mes. */
export function valueTooltip(range, sums) {
    const rotulo = rangeLabel(range, sums);
    return sums && +range.start !== +range.end ? `Acumulado de ${rotulo}` : rotulo;
}

/**
 * Junta as comparacoes mensais num valor por serie.
 *
 * `monthly[i]` e a comparacao do i-esimo mes dos dois lados (como `CalculatingMachine.comparation`
 * devolve: um item por serie, com `current` do lado base e `prev` do comparado). Os dois lados tem o
 * mesmo numero de meses. Quem nao soma (ou com `sumMonths` falso) pega o ultimo mes.
 *
 * @returns {Array<{ _id: string, current: number, prev: number, sums: boolean }>}
 */
export function mergeMonthlyComparations(
    monthly = [],
    series = [],
    { sumMonths = true } = {}
) {
    const ultimo = monthly[monthly.length - 1] || [];
    return series.map((serie) => {
        const doMes = (mes) =>
            (mes || []).find((item) => item._id === serie._id) || {};
        const sums = sumMonths && sumsMonthByMonth(serie);
        if (!sums) {
            const item = doMes(ultimo);
            return {
                _id: serie._id,
                current: Number(item.current) || 0,
                prev: Number(item.prev) || 0,
                sums
            };
        }
        let current = 0;
        let prev = 0;
        for (const mes of monthly) {
            const item = doMes(mes);
            current += Number(item.current) || 0;
            prev += Number(item.prev) || 0;
        }
        return { _id: serie._id, current, prev, sums };
    });
}
